(function () {
  const GCC_COUNTRY_TO_CURRENCY = {
    AE: { code: 'AED', symbol: 'د.إ' },
    BH: { code: 'BHD', symbol: 'د.ب' },
    KW: { code: 'KWD', symbol: 'د.ك' },
    OM: { code: 'OMR', symbol: 'ر.ع.' },
    QA: { code: 'QAR', symbol: 'ر.ق' },
    SA: { code: 'SAR', symbol: 'ر.س' },
  };

  const CURRENCY_DECIMALS = {
    AED: 2,
    BHD: 3,
    KWD: 3,
    OMR: 3,
    QAR: 2,
    SAR: 2,
  };

  const FALLBACK_RATES_FROM_OMR = {
    AED: 9.55,
    BHD: 0.978,
    KWD: 0.796,
    OMR: 1,
    QAR: 9.47,
    SAR: 9.75,
  };

  const MONEY_TEXT_SELECTOR =
    'body *:not(script):not(style):not(noscript):not(template):not(select):not(option):not(textarea):not(input)';
  const LOCALIZATION_SELECTOR =
    'localization-form-component, dropdown-localization-component, drawer-localization-component';
  const CACHE_KEY = 'ceilo:gccCurrencyRates:v1';
  const CACHE_TTL = 6 * 60 * 60 * 1000;
  const originalTextByNode = new WeakMap();

  const config = window.CeiloGccCurrency || {};
  const sourceCurrency = (config.sourceCurrency || 'OMR').toUpperCase();
  let activeCurrency = GCC_COUNTRY_TO_CURRENCY.OM;
  let rates = { ...FALLBACK_RATES_FROM_OMR };
  let scheduled = false;

  function selectedCountryCode() {
    const configuredCountry = (config.countryCode || '').toUpperCase();
    const formCountry = document.querySelector('input[name="country_code"]')?.value?.toUpperCase();
    const storedCountry = localStorage.getItem('ceilo:gccCountry')?.toUpperCase();

    return formCountry || configuredCountry || storedCountry || '';
  }

  async function detectVisitorCountry() {
    const selectedCountry = selectedCountryCode();
    if (selectedCountry && selectedCountry !== 'OM' && GCC_COUNTRY_TO_CURRENCY[selectedCountry]) return selectedCountry;

    try {
      const response = await fetch('https://get.geojs.io/v1/ip/country.json', {
        cache: 'no-store',
      });
      if (!response.ok) return selectedCountry;

      const data = await response.json();
      const visitorCountry = String(data.country || '').toUpperCase();
      return GCC_COUNTRY_TO_CURRENCY[visitorCountry] ? visitorCountry : selectedCountry;
    } catch (_error) {
      return selectedCountry;
    }
  }

  function readCachedRates() {
    try {
      const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
      if (!cached || Date.now() - cached.timestamp > CACHE_TTL) return null;
      return cached.rates;
    } catch (_error) {
      return null;
    }
  }

  async function loadRates() {
    const cachedRates = readCachedRates();
    if (cachedRates) {
      rates = { ...rates, ...cachedRates };
      return;
    }

    try {
      const response = await fetch(`https://open.er-api.com/v6/latest/${sourceCurrency}`, {
        cache: 'no-store',
      });
      if (!response.ok) return;

      const data = await response.json();
      if (data.result !== 'success' || !data.rates) return;

      const nextRates = {};
      Object.values(GCC_COUNTRY_TO_CURRENCY).forEach(({ code }) => {
        if (typeof data.rates[code] === 'number') nextRates[code] = data.rates[code];
      });

      if (Object.keys(nextRates).length === 0) return;

      rates = { ...rates, ...nextRates };
      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({
          timestamp: Date.now(),
          rates: nextRates,
        })
      );
    } catch (_error) {
      // Fallback rates keep the display converter working when the API is unavailable.
    }
  }

  function parseMoneyAmount(value, currency) {
    const decimals = CURRENCY_DECIMALS[currency] ?? 2;
    const cleaned = value.replace(/[^\d.,]/g, '');
    const separators = [...cleaned.matchAll(/[.,]/g)].map((match) => match.index);

    if (separators.length === 0) return Number(cleaned);

    const lastSeparator = separators[separators.length - 1];
    const fraction = cleaned.slice(lastSeparator + 1);

    if (fraction.length === decimals) {
      const integer = cleaned.slice(0, lastSeparator).replace(/[.,]/g, '');
      return Number(`${integer}.${fraction}`);
    }

    return Number(cleaned.replace(/[.,]/g, ''));
  }

  function formatMoneyAmount(amount, currency) {
    const decimals = CURRENCY_DECIMALS[currency] ?? 2;
    return `${new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(amount)} ${currency}`;
  }

  function convertAmount(amount, targetCurrency) {
    const rate = rates[targetCurrency] || FALLBACK_RATES_FROM_OMR[targetCurrency] || 1;
    return amount * rate;
  }

  function convertMoneyText(text) {
    const targetCurrency = activeCurrency.code;
    if (targetCurrency === sourceCurrency) return text;

    const sourcePattern = new RegExp(`([0-9][0-9.,]*)\\s*${sourceCurrency}\\b`, 'g');

    return text.replace(sourcePattern, (_match, amountText) => {
      const amount = parseMoneyAmount(amountText, sourceCurrency);
      if (!Number.isFinite(amount)) return _match;
      return formatMoneyAmount(convertAmount(amount, targetCurrency), targetCurrency);
    });
  }

  function shouldSkipNode(node) {
    const parent = node.parentElement;
    if (!parent) return true;
    if (parent.closest('[data-gcc-currency-ignore]')) return true;
    if (parent.closest(LOCALIZATION_SELECTOR)) return true;
    return !new RegExp(`\\b${sourceCurrency}\\b`).test(node.nodeValue || '');
  }

  function updatePriceText() {
    const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        return shouldSkipNode(node) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT;
      },
    });

    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);

    nodes.forEach((node) => {
      if (!originalTextByNode.has(node)) originalTextByNode.set(node, node.nodeValue);
      const originalText = originalTextByNode.get(node);
      node.nodeValue = convertMoneyText(originalText);
    });
  }

  function updateLocalizationUi() {
    document.querySelectorAll('.currency-code').forEach((element) => {
      element.textContent = activeCurrency.code;
    });

    document.querySelectorAll('.localization-form__list-item[data-value]').forEach((countryItem) => {
      const currency = GCC_COUNTRY_TO_CURRENCY[countryItem.dataset.value];
      const currencyLabel = countryItem.querySelector('.localization-form__currency');
      if (!currency || !currencyLabel) return;

      currencyLabel.textContent = `${currency.code} ${currency.symbol}`;
      currencyLabel.hidden = false;
    });
  }

  function refreshDisplay() {
    updateLocalizationUi();
    updatePriceText();
  }

  function scheduleRefresh() {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(() => {
      scheduled = false;
      refreshDisplay();
    });
  }

  function observeChanges() {
    const observer = new MutationObserver((mutations) => {
      if (
        mutations.some((mutation) =>
          [...mutation.addedNodes].some((node) => node.nodeType === Node.ELEMENT_NODE || node.nodeType === Node.TEXT_NODE)
        )
      ) {
        scheduleRefresh();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  async function init() {
    const countryCode = await detectVisitorCountry();
    if (GCC_COUNTRY_TO_CURRENCY[countryCode]) {
      activeCurrency = GCC_COUNTRY_TO_CURRENCY[countryCode];
      localStorage.setItem('ceilo:gccCountry', countryCode);
    }

    refreshDisplay();
    await loadRates();
    refreshDisplay();
    observeChanges();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
